import { useMemo, useState } from 'react';
import {
  IconButton,
  Box,
  TextField,
  DialogContentText,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { AllowedRepositories } from '../types';
import { Table } from '@backstage/core-components';
import {
  CATALOG_FILTER_EXISTS,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';
import Stack from '@mui/material/Stack';
import { CustomTooltip } from '../CustomTooltip';

interface EditDeploymentRoleRepositoriesProps {
  allowedRepositories: AllowedRepositories[];
  onRemoveRepo: (slug: string) => void;
  onAddRepo: (slug: string) => void;
}

export function EditDeploymentRoleRepositories({
  allowedRepositories: allowedRepositories,
  onRemoveRepo: onRemoveRepo,
  onAddRepo: onAddRepo,
}: EditDeploymentRoleRepositoriesProps) {
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);

  const catalogApi = useApi(catalogApiRef);

  const { value: facetsResponse } = useAsync(() => {
    return catalogApi.getEntityFacets({
      facets: ['metadata.namespace'],
      filter: {
        kind: ['Component'],
        'metadata.annotations.github.com/project-slug': CATALOG_FILTER_EXISTS,
      },
    });
  }, [catalogApi]);

  const orgs = useMemo(() => {
    return (
      facetsResponse?.facets?.['metadata.namespace']
        .filter((facet: { value: string | string[] }) =>
          facet.value.includes('-emu'),
        )
        .map((facet: { value: any }) => facet.value) || []
    );
  }, [facetsResponse]);

  const handleAddRepo = () => {
    if (
      selectedRepo &&
      orgs.some((org: any) => selectedRepo.startsWith(`${org}/`))
    ) {
      onAddRepo(selectedRepo);
      setSelectedRepo('');
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  return (
    <Stack spacing={2} width="100%">
      <DialogContentText>
        To add a new repository, enter the repository name including the github
        org in the text field and click the Add icon. To remove an existing
        repository, click the delete icon next to the repository in the list.
        Confirm your updates by clicking the Confirm button. There is a limit of
        25 repositories.
      </DialogContentText>
      <Stack direction="row" spacing={1} alignItems="center">
        <CustomTooltip detail="Example: principalinformationservices-emu/aws-workload-repo" />
        <Box flexGrow={1}>
          <TextField
            name="foo"
            label="New Repository"
            error={!isValid}
            helperText={
              !isValid
                ? 'Invalid repository, must include a valid github organization'
                : null
            }
            value={selectedRepo || ''}
            variant="outlined"
            onChange={e => setSelectedRepo(e.target.value)}
            fullWidth
            disabled={allowedRepositories.length >= 25}
          />
        </Box>
        <Box>
          <IconButton
            color="primary"
            onClick={handleAddRepo}
            disabled={!selectedRepo || allowedRepositories.length >= 25}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Stack>
      <Table
        columns={[
          {
            title: 'Repository',
            field: 'slug',
            width: 'fullWidth',
          },
        ]}
        data={allowedRepositories}
        options={{ search: false, padding: 'dense' }}
        title="Allowed Repositories"
        actions={[
          {
            icon: () => <DeleteIcon />,
            tooltip: 'Remove Repository',
            onClick: (_event, rowData) =>
              onRemoveRepo((rowData as AllowedRepositories).slug),
          },
        ]}
      />
    </Stack>
  );
}
